/**
 * Pure utility functions and shared types for the Reports data derivation layer.
 *
 * All functions here are pure (no React, no providers, no side effects) and
 * are exported for use by useReportsData.ts. Keeping them separate enables
 * straightforward unit testing and avoids bloating the hook file.
 */

import { subDays } from 'date-fns';

import { Trend } from '@/graphql';
import { deriveTrend } from '@/helpers/deriveTrend';
import { ReportsDateRange } from '@/hooks/useReportsAccess';

// ─────────────────────────────────────────────────────────────
// Shared type: ChartSectionData
// ─────────────────────────────────────────────────────────────

/**
 * Generic container for a single chart section.
 * Every chart in Reports returns this shape so that Phase 25 components
 * can uniformly check `emptyReason` before rendering.
 */
export interface ChartSectionData<T> {
	data: T[];
	emptyReason: 'no-history' | 'date-filtered' | null;
}

// ─────────────────────────────────────────────────────────────
// Generational constants
// ─────────────────────────────────────────────────────────────

/**
 * Ordered list of generational labels used for both bucketing and filter chips.
 * The `as const` assertion makes this a readonly tuple so TypeScript can derive
 * the `Generation` union type from it.
 *
 * Boundaries (confirmed in PLAN.md):
 *   Gen Z:      1997–2012
 *   Millennial: 1981–1996
 *   Gen X:      1965–1980
 *   Boomer:     1946–1964
 *   Unknown:    null / undefined / outside all ranges
 */
export const GENERATIONS = ['Gen Z', 'Millennial', 'Gen X', 'Boomer', 'Unknown'] as const;

export type Generation = typeof GENERATIONS[number];

/**
 * The canonical 8 Plutchik emotion labels — the only valid values in
 * analysisOutput.emotions[].emotion. Order matches the Plutchik wheel.
 */
export const EMOTIONS = [
	'Joy', 'Trust', 'Fear', 'Surprise', 'Sadness', 'Disgust', 'Anger', 'Anticipation',
] as const;

export type Emotion = typeof EMOTIONS[number];

// ─────────────────────────────────────────────────────────────
// getDateBounds
// ─────────────────────────────────────────────────────────────

/**
 * Converts a `ReportsDateRange` discriminant into concrete Date boundaries.
 *
 * - 'last-30' → last 30 calendar days (subDays handles month/year wrapping)
 * - 'last-90' → last 90 calendar days
 * - 'year'    → Jan 1 to Dec 31 of `year` (defaults to current calendar year).
 *               When `year` refers to a past year the window is the full year,
 *               not truncated to today — so historical data is fully included.
 * - 'full'    → epoch (new Date(0)) to today — effectively "all time"
 *
 * `to` for rolling ranges (last-30, last-90, full) is always a fresh
 * `new Date()` so the window stays relative to the current moment.
 */
export function getDateBounds(
	dateRange: ReportsDateRange,
	year?: number,
): { from: Date; to: Date } {
	const today = new Date();
	const calYear = year ?? today.getFullYear();

	switch (dateRange) {
		case 'last-30':
			return { from: subDays(today, 30), to: today };
		case 'last-90':
			return { from: subDays(today, 90), to: today };
		case 'year':
			return { from: new Date(calYear, 0, 1), to: new Date(calYear, 11, 31) };
		case 'full':
		default:
			return { from: new Date(0), to: today };
	}
}

// ─────────────────────────────────────────────────────────────
// birthYearToGeneration
// ─────────────────────────────────────────────────────────────

/**
 * Maps a raw birth year integer to a generational label.
 *
 * Returns 'Unknown' for null, undefined, or birth years outside all defined
 * generational ranges. Boundaries are fixed calendar years, not rolling ages.
 *
 * Use the returned `Generation` strings as values in generationFilter chip
 * options so that chip selection and derivation always match exactly.
 */
export function birthYearToGeneration(birthYear: number | null | undefined): Generation {
	if (birthYear == null) return 'Unknown';
	if (birthYear >= 1997 && birthYear <= 2012) return 'Gen Z';
	if (birthYear >= 1981 && birthYear <= 1996) return 'Millennial';
	if (birthYear >= 1965 && birthYear <= 1980) return 'Gen X';
	if (birthYear >= 1946 && birthYear <= 1964) return 'Boomer';
	return 'Unknown';
}

// ─────────────────────────────────────────────────────────────
// resolveEmptyReason
// ─────────────────────────────────────────────────────────────

/**
 * Determines the empty reason for a chart section.
 *
 * Logic:
 *   - filteredCount > 0              → null (chart has data to render)
 *   - filteredCount === 0, unfilteredCount > 0 → 'date-filtered'
 *     (data exists historically but none falls in the active date range)
 *   - filteredCount === 0, unfilteredCount === 0 → 'no-history'
 *     (no data at all — user has never submitted feedback, or no team data)
 *
 * @param filteredCount   Number of data points after all active filters applied
 * @param unfilteredCount Number of data points with NO date/scope/chip filtering
 */
export function resolveEmptyReason(
	filteredCount: number,
	unfilteredCount: number,
): 'no-history' | 'date-filtered' | null {
	if (filteredCount > 0) return null;
	if (unfilteredCount > 0) return 'date-filtered';
	return 'no-history';
}

// ─────────────────────────────────────────────────────────────
// makeSection
// ─────────────────────────────────────────────────────────────

/**
 * Convenience wrapper that pairs a filtered data array with its `emptyReason`.
 *
 * Accepts two arrays of the same type:
 *   `filtered`   — data after ALL active filters (date, scope, chips)
 *   `unfiltered` — data with NO filters (used only to compute emptyReason)
 *
 * Returns a `ChartSectionData<T>` ready for Phase 25 chart components.
 */
export function makeSection<T>(filtered: T[], unfiltered: T[]): ChartSectionData<T> {
	return {
		data: filtered,
		emptyReason: resolveEmptyReason(filtered.length, unfiltered.length),
	};
}

// ─────────────────────────────────────────────────────────────
// SubmissionRecord — shared flat submission type
// ─────────────────────────────────────────────────────────────

/**
 * A single feedback submission flattened from a setlist's submissions array.
 *
 * Moved here from useReportsData.ts so that pure helper functions
 * (e.g. deriveTopTen) can use the type without importing from the hook.
 */
export interface SubmissionRecord {
	setlistId: string;
	setlistStartAt: string;   // ISO date string from setlist.startAt
	serviceId: string | null | undefined;
	teamSongId: string;
	personalScore: number;
	teamScore: number;
	congregationalScore: number;
	userId: string;
}

// ─────────────────────────────────────────────────────────────
// TopTenEntry — data shape for the Top 10 section
// ─────────────────────────────────────────────────────────────

/**
 * A single ranked entry in the Top 10 section.
 *
 * `compositeScore` is the mean of all three score fields across all filtered
 * submissions for this song — same formula as user top team songs.
 * `trend` is derived via OLS regression over per-setlist means.
 */
export interface TopTenEntry {
	teamSongId: string;
	name: string;
	artist: string;
	artworkUrl: string | null | undefined;
	compositeScore: number;
	trend: Trend;
}

// ─────────────────────────────────────────────────────────────
// deriveTopTen — pure ranking function
// ─────────────────────────────────────────────────────────────

/**
 * Derives up to 10 top-ranked songs from a pre-filtered submissions array.
 *
 * Algorithm:
 *   1. Group submissions by teamSongId
 *   2. For each group: compute compositeScore (flatMap all three score fields,
 *      mean across the entire array — same formula as user top team songs)
 *   3. For each group: derive trend by grouping submissions per setlist,
 *      computing mean scores per setlist, sorting by startAt, and calling
 *      deriveTrend() with the resulting SentimentReport[]
 *   4. Skip songs whose teamSongId is not in songMap (orphaned submissions)
 *   5. Sort descending by compositeScore; name-ascending tie-breaker
 *   6. Return first 10 entries
 *
 * IMPORTANT: Do NOT re-apply filter logic here. The caller must pass
 * already-filtered submissions (filteredSubmissions from useReportsData).
 * Adding filter logic here would create inconsistency with other chart derivations.
 *
 * @param submissions - Pre-filtered SubmissionRecord array
 * @param songMap     - O(1) teamSongId → song metadata lookup
 */
export function deriveTopTen(
	submissions: SubmissionRecord[],
	songMap: Map<string, { id: string; name: string; artist: string; artworkUrl?: string | null }>,
): TopTenEntry[] {
	// 1. Group submissions by teamSongId
	const bySong = new Map<string, SubmissionRecord[]>();
	for (const sub of submissions) {
		const existing = bySong.get(sub.teamSongId);
		if (existing) {
			existing.push(sub);
		} else {
			bySong.set(sub.teamSongId, [sub]);
		}
	}

	const entries: TopTenEntry[] = [];

	bySong.forEach((subs, songId) => {
		// 2a. Composite score: flatten all three score fields, mean across the array
		const allValues = subs.flatMap((s) => [s.personalScore, s.teamScore, s.congregationalScore]);
		const compositeScore = allValues.reduce((a, b) => a + b, 0) / allValues.length;

		// 2b. Trend: group by setlistId, compute per-setlist means, call deriveTrend()
		const bySetlist = new Map<string, {
			personalSum: number;
			teamSum: number;
			congregationalSum: number;
			count: number;
			startAt: string;
		}>();
		for (const sub of subs) {
			const existing = bySetlist.get(sub.setlistId) ?? {
				personalSum: 0,
				teamSum: 0,
				congregationalSum: 0,
				count: 0,
				startAt: sub.setlistStartAt,
			};
			bySetlist.set(sub.setlistId, {
				personalSum: existing.personalSum + sub.personalScore,
				teamSum: existing.teamSum + sub.teamScore,
				congregationalSum: existing.congregationalSum + sub.congregationalScore,
				count: existing.count + 1,
				startAt: sub.setlistStartAt,
			});
		}

		const reports = [...bySetlist.values()]
			.sort((a, b) => a.startAt.localeCompare(b.startAt))
			.map((v) => ({
				meanPersonalScore: v.personalSum / v.count,
				meanTeamScore: v.teamSum / v.count,
				meanCongregationalScore: v.congregationalSum / v.count,
			}));

		const trend = deriveTrend(reports);

		// 2c. Look up song metadata — skip orphaned submissions
		const song = songMap.get(songId);
		if (!song) return;

		// 2d. Build entry
		entries.push({
			teamSongId: songId,
			name: song.name,
			artist: song.artist,
			artworkUrl: song.artworkUrl ?? null,
			compositeScore,
			trend,
		});
	});

	// 3. Sort descending by compositeScore; name-ascending tie-breaker
	entries.sort((a, b) => {
		const scoreDiff = b.compositeScore - a.compositeScore;
		if (scoreDiff !== 0) return scoreDiff;
		return a.name.localeCompare(b.name);
	});

	// 4. Return first 10
	return entries.slice(0, 10);
}
